import { useCallback, useRef, useState } from 'react'

import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import StopIcon from '@mui/icons-material/Stop'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import type { SxProps, Theme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'

const TEST_DURATION_MS = 2000
const TEST_FREQUENCY = 440 // A4 note

export type AudioOutputTestProps = {
  deviceId: string
  sx?: SxProps<Theme>
}

export function AudioOutputTest({ deviceId, sx = [] }: AudioOutputTestProps) {
  const { t } = useTranslation()
  const [isPlaying, setIsPlaying] = useState(false)
  const [hasError, setHasError] = useState(false)
  const audioContextRef = useRef<AudioContext | null>(null)
  const audioElementRef = useRef<HTMLAudioElement | null>(null)
  const oscillatorRef = useRef<OscillatorNode | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const stop = useCallback(() => {
    try { oscillatorRef.current?.stop() } catch { /* already stopped */ }
    oscillatorRef.current = null
    if (audioElementRef.current) {
      audioElementRef.current.pause()
      audioElementRef.current.srcObject = null
      audioElementRef.current = null
    }
    audioContextRef.current?.close()
    audioContextRef.current = null
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setIsPlaying(false)
  }, [])

  const play = useCallback(async () => {
    if (isPlaying) {
      stop()
      return
    }

    setHasError(false)

    try {
      const audioContext = new AudioContext()
      audioContextRef.current = audioContext

      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      oscillator.type = 'sine'
      oscillator.frequency.setValueAtTime(TEST_FREQUENCY, audioContext.currentTime)

      // Fade in/out to avoid clicks
      gainNode.gain.setValueAtTime(0, audioContext.currentTime)
      gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.05)
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + TEST_DURATION_MS / 1000)

      // Route through a MediaStreamDestination → HTMLAudioElement so we can use setSinkId
      const destination = audioContext.createMediaStreamDestination()
      oscillator.connect(gainNode)
      gainNode.connect(destination)

      const audioElement = new Audio()
      audioElement.srcObject = destination.stream

      // Route to the selected output device
      if (typeof audioElement.setSinkId === 'function') {
        await audioElement.setSinkId(deviceId)
      }

      await audioElement.play()
      audioElementRef.current = audioElement
      oscillatorRef.current = oscillator

      oscillator.start()
      setIsPlaying(true)

      timeoutRef.current = setTimeout(() => {
        stop()
      }, TEST_DURATION_MS)
    } catch (err) {
      console.warn(`Could not test audio output ${deviceId}:`, err)
      setHasError(true)
      setIsPlaying(false)
      // Clean up partial state
      audioContextRef.current?.close()
      audioContextRef.current = null
    }
  }, [deviceId, isPlaying, stop])

  const tooltipText = hasError
    ? t('component.ui.deviceSelectionDialog.deviceUnavailable')
    : t('component.ui.deviceSelectionDialog.testAudioOutput')

  return (
    <Tooltip title={tooltipText}>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation()
          play()
        }}
        color={hasError ? 'error' : isPlaying ? 'error' : 'primary'}
        sx={[{ flexShrink: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        {hasError ? <ErrorOutlineIcon fontSize="small" /> : isPlaying ? <StopIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  )
}

export default AudioOutputTest
